/** Main page copy — why → how → proof. Keep it short; diagrams carry the idea. */

export const page = {
  title: {
    primary: "Write React.",
    secondary: "Ship lean JavaScript.",
  },
  /** The stop-and-stare line. */
  wow: "JetShake is the full compiler React has been missing.",

  intro: [
    "It compiles React components, and even full applications into highly optimised JavaScript and HTML, with no virtual DOM or React runtime.",
    "The result is signal-driven state, fine-grained updates, and only the minimum code your application actually needs.",
  ],

  why: [
    {
      heading: "React already won.",
      figure: "ecosystem",
      paragraphs: [
        "The ecosystem, the libraries, the AI tooling and knowledge.",
        "There's never been a better time to build with React.",
      ],
      afterFigure: [
        "But every React app ships an entire rendering engine. Even the simplest pages pay for it.",
      ],
    },
    {
      heading: "Shipping has a cost.",
      figure: "cost",
      paragraphs: [
        "Every app ships a runtime.",
        "Updates go through a virtual DOM.",
        "Solid and Svelte proved you can compile ahead and ship less - just not while staying in React.",
        "JetShake removes that trade-off.",
      ],
    },
  ],

  /** Visual weight comparison under "Shipping has a cost." */
  costFigure: {
    left: {
      name: "React",
      kb: 220,
      layers: ["Reconciler", "Virtual DOM", "Runtime", "Your page"],
    },
    right: {
      name: "JetShake",
      kb: 18,
      layers: ["Fine-grained updates", "Ship less JavaScript"],
    },
  },

  pivot: {
    heading: {
      primary: "Keep React.",
      secondary: "Change what you ship.",
    },
    lines: [
      "Write ordinary React.",
      "Keep your components and libraries.",
      "Ship lean JavaScript instead.",
    ],
    figure: "keep",
  },

  live: {
    heading: {
      primary: "Wait..",
      secondary: "this actually works?",
    },
    lead: "This whole page is composed purely in React, including these ShadcnUI components below. Yet there's no react-dom in your network tab! ",
  },

  adapters: {
    heading: {
      primary: "React for any stack.",
      secondary: "Keep your existing framework.",
    },
    lead: "If you don't use React, you can still use JetShake to compile individual React components - opening up access to the whole of the React ecosystem, regardless of your framework!",
    cli: "$ npx jetshake export combobox.tsx",
    source: "React Component",
    output: "Optimised JavaScript",
    stacks: [
      {
        key: "wc",
        label: "Web Component",
        title: "index.html",
        code: `
<script type="module" src="/js/jet-combobox.js"></script>

<jet-combobox
  options='["Ada","Grace","Edsger"]'
  onchange="save(event.detail)">
</jet-combobox>`,
      },
      {
        key: "blade",
        label: "Laravel Blade",
        title: "dashboard.blade.php",
        code: `
{{-- generated wrapper mirrors the manifest --}}
<script type="module" src="/js/jet-combobox.js"></script>

<jet-combobox
  options='@json($users)'
  x-on:change="selected = $event.detail">
</jet-combobox>`,
      },
      {
        key: "rails",
        label: "Rails",
        title: "app/views/dashboard.html.erb",
        code: `
<%# drop the compiled tag into any ERB template %>
<%= javascript_include_tag "jet-combobox", type: "module" %>

<jet-combobox
  options='<%= @users.to_json %>'
  onchange="selected = event.detail">
</jet-combobox>`,
      },
      {
        key: "alpine",
        label: "Alpine",
        title: "page.html",
        code: `
<!-- events are plain DOM events - Alpine just listens -->
<div x-data="{ user: null }">
  <jet-combobox options='["Ada","Grace","Edsger"]'
    x-on:change="user = $event.detail"></jet-combobox>
  <p x-text="user"></p>
</div>`,
      },
      {
        key: "astro",
        label: "Astro",
        title: "index.astro",
        code: `
---
// no integration, no client: directive, just a tag
const users = await getUsers()
---
<script type="module" src="/jet-combobox.js"></script>

<jet-combobox options={JSON.stringify(users)}></jet-combobox>`,
      },
      {
        key: "html",
        label: "Plain JS",
        title: "index.html",
        code: `
<!-- any static page. no build, no bundler, no framework -->
<script type="module" src="jet-combobox.js"></script>

<jet-combobox
  options='["Ada","Grace"]'
  onchange="save(event.detail)">
</jet-combobox>`,
      },
    ],
  },

  next: {
    heading: "What's next?",
    paragraphs: [
      "JetShake will be open source. Component compilation is almost here.\nFull application support with Vite plugin is coming soon.",
    ],
    contact:
      "Want to test JetShake, contribute, sponsor the project, or make a donation? Get in touch - drop your Email below.",
  },

  close: {
    lines: [
      { text: "Use React.", tone: "fg" },
      { text: "Compile with JetShake.", tone: "muted" },
    ],
    cta: "Want to get notified when JetShake launches?",
    placeholder: "you@company.com",
    button: "Get launch updates",
    loading: "Joining...",
    done: "You've been signed up.",
    fine: "Zero spam. Unsubscribe in one click.",
  },
} as const;

export const ecoLibs = [
  { name: "shadcn/ui", logo: "shadcn" },
  { name: "Motion", logo: "motion" },
  { name: "TanStack", logo: "tanstack" },
  { name: "React Router", logo: "react-router" },
  { name: "Base UI", logo: "base-ui" },
  { name: "React Query", logo: "react-query" },
  { name: "Zod", logo: "zod" },
  { name: "Zustand", logo: "zustand.png" },
  { name: "Jotai", logo: "jotai.png" },
  { name: "Mantine", logo: "mantine" },
  { name: "Ark UI", logo: "ark" },
  { name: "Magic UI", logo: "magic-ui" },
  { name: "Reverse UI", logo: "reverse-ui.png" },
] as const;
